import { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface NumberKpiProps {
  title: string;
  // number => formatado como inteiro pt-BR; ReactNode (ex.: moeda) => renderizado como veio
  value: ReactNode;
  icon: React.ElementType;
  // Padronizado 'red' para 'destructive'
  variant?: "blue" | "emerald" | "purple" | "destructive" | "amber" | "default";
  loading?: boolean;
  /** Linha auxiliar abaixo do valor (ex.: aging). */
  hint?: ReactNode;
  /** Presente => o card vira botão clicável. */
  onClick?: () => void;
  /** Elemento extra ao lado do título (ex.: `HelpIcon` explicando o cálculo). */
  titleExtra?: ReactNode;
}

// 🚀 Movido para fora: alocado na memória apenas uma vez na inicialização do JS
const colorMap = {
  default: "text-primary bg-primary/10 border-primary/20",
  blue: "text-blue-600 bg-blue-500/10 border-blue-200 dark:text-blue-400 dark:border-blue-800",
  emerald:
    "text-emerald-600 bg-emerald-500/10 border-emerald-200 dark:text-emerald-400 dark:border-emerald-800",
  purple:
    "text-purple-600 bg-purple-500/10 border-purple-200 dark:text-purple-400 dark:border-purple-800",
  amber:
    "text-amber-600 bg-amber-500/10 border-amber-200 dark:text-amber-400 dark:border-amber-800",
  destructive: "text-red-600 bg-red-500/10 border-red-200 dark:text-red-400 dark:border-red-800",
};

// Utilitário também pode ficar de fora se não depender do escopo local
const formatNumber = (val: number) => new Intl.NumberFormat("pt-BR").format(val);

export function NumberKpiCard({
  title,
  value,
  icon: Icon,
  variant = "default",
  loading,
  hint,
  onClick,
  titleExtra,
}: NumberKpiProps) {
  const Root = onClick ? "button" : "div";
  return (
    <Root
      {...(onClick ? { type: "button" as const, onClick } : {})}
      className={cn(
        "relative w-full overflow-hidden rounded-xl border border-border bg-card p-5 text-left shadow-sm transition-all hover:shadow-md",
        onClick &&
          "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
            <span className="truncate">{title}</span>
            {titleExtra}
          </p>
          <div className="mt-2 flex items-baseline gap-2">
            {loading ? (
              <div className="h-8 w-24 animate-pulse rounded bg-muted" />
            ) : (
              <span className="truncate text-2xl font-bold tabular-nums tracking-tight text-foreground">
                {typeof value === "number" ? formatNumber(value) : value}
              </span>
            )}
          </div>
          {hint && !loading && <p className="mt-1 truncate text-xs font-medium">{hint}</p>}
        </div>
        <div className={cn("shrink-0 rounded-lg border p-2.5", colorMap[variant])}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Root>
  );
}
